'use client';

import { ISbStoryData } from 'storyblok-js-client';
import { clsx } from 'clsx';

import { startTransition, useCallback, useMemo, useState } from 'react';

import { LicenseStoryblok, Locales } from '@monoimin/sb-types';

import { Carousel } from '@monoimin/molecules/carousel';

import { useParamsUpdater, useSnapCarousel } from '@monoimin/hooks';

import { PaginationWrapper } from '@monoimin/molecules/pagination/PaginationWrapper';
import { Modal } from '@monoimin/molecules/Modal';
import { ModalInterface } from '@monoimin/molecules/carousel/ModalInterface';

import { SliderCardWrapper } from '../../components/SliderCardWrapper';

import { LicenseCard } from './LicenseCard';

interface FullscreenCarouselProps {
  licenses: ISbStoryData<LicenseStoryblok>[];
  locale: Locales;
  variant: 'slider' | 'grid';
}

export function LicenseFullscreenCarousel({
  licenses,
  locale,
  variant,
}: FullscreenCarouselProps) {
  const { searchParams, onChange: onChangeParams } = useParamsUpdater();

  const paramsData = useMemo(() => {
    return licenses.map((item) => {
      return item.slug;
    });
  }, [licenses]);

  const paramsLicense = searchParams?.get('license') || '';

  const isValidParamsLicense = useMemo(() => {
    return paramsData.includes(paramsLicense);
  }, [paramsData, paramsLicense]);

  const [modalOpen, setModalOpen] = useState(isValidParamsLicense);

  const [initialIndex, setInitialIndex] = useState<number>(() => {
    return isValidParamsLicense ? paramsData.indexOf(paramsLicense) : 0;
  });

  const { scrollRef, activePageIndex, prev, next, goTo } = useSnapCarousel({
    initialIndex: initialIndex,
  });

  const onChangeLicenseParam = useCallback(
    (param?: string) => {
      startTransition(() => {
        onChangeParams({ license: param });
      });
    },
    [onChangeParams]
  );

  const onCardClick = useCallback(
    (index: number, param?: string) => {
      startTransition(() => {
        setInitialIndex(index);
        setModalOpen(true);
        onChangeLicenseParam(param);
      });
    },
    [onChangeLicenseParam]
  );

  const modalContent = useMemo(() => {
    return licenses.map((item) => {
      const { uuid } = item;

      return (
        <LicenseCard
          key={uuid}
          data={item}
          locale={locale}
          classNames={{
            container:
              'snap-center  min-w-[90vw] snap-always shrink-0 cursor-pointer',
            title: 'hidden',
            description: 'hidden',
            image: 'w-auto h-full',
          }}
        />
      );
    });
  }, [licenses, locale]);

  const paginationContent = useMemo(() => {
    return licenses.map((license, index) => {
      const { uuid } = license;

      const isActive = activePageIndex === index;

      return (
        <LicenseCard
          dataPaginationActive={isActive}
          key={uuid}
          data={license}
          locale={locale}
          classNames={{
            container: clsx(
              'w-15 h-20 duration-300 snap-center hover:scale-110',
              {
                'opacity-50 cursor-pointer': !isActive,
              }
            ),
            title: 'hidden',
            description: 'hidden',
          }}
          onClick={() => {
            goTo(index, 'instant');
            onChangeLicenseParam(license.slug);
          }}
        />
      );
    });
  }, [licenses, activePageIndex, goTo, locale, onChangeLicenseParam]);

  return (
    <>
      <Modal
        isOpen={modalOpen}
        setIsOpen={setModalOpen}
        panelContainerClassName='flex items-center justify-center'
      >
        <ModalInterface
          onNext={next}
          onPrev={prev}
          onClose={() => {
            setModalOpen(false);
            startTransition(() => {
              onChangeParams({ license: '' });
            });
          }}
        >
          <div
            ref={scrollRef}
            className='overflow-x-auto flex snap-x snap-mandatory scrollbar-hide relative w-[90vw] mx-auto h-[80vh]'
          >
            {modalContent}
          </div>
          <PaginationWrapper
            active={activePageIndex}
            max={licenses.length}
            className='flex gap-5 mx-auto w-fit max-w-screen px-4 mt-5'
          >
            {paginationContent}
          </PaginationWrapper>
        </ModalInterface>
      </Modal>
      {variant === 'slider' && (
        <Carousel slidesCount={licenses.length}>
          {licenses.map((license, index) => {
            return (
              <SliderCardWrapper key={license.slug} withStandardHeight={false}>
                <LicenseCard
                  onClick={() => onCardClick(index, license.slug)}
                  data={license}
                  locale={locale || 'en'}
                  classNames={{
                    container: 'aspect-[55/40] w-full',
                    image: 'object-left',
                  }}
                />
              </SliderCardWrapper>
            );
          })}
        </Carousel>
      )}
      {variant === 'grid' && (
        <div className='container grid lg:grid-cols-4 cursor-pointer max-md:grid-cols-2 max-lg:grid-cols-3 mb-15 gap-x-6 gap-y-18 max-md:gap-y-14 max-md:gap-x-4'>
          {licenses.map((license, index) => {
            const { uuid } = license;

            return (
              <LicenseCard
                key={uuid}
                onClick={() => onCardClick(index, license.slug)}
                data={license}
                locale={locale}
              />
            );
          })}
        </div>
      )}
    </>
  );
}
